import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DSBackLink } from '../../design-system/primitives';
import { authService } from '../../services/authService';
import { getJson, postJson, del } from './scorekeepingApi';
import PublicHeader from './PublicHeader';
import { formatScore, ordinal, inWords } from './format';
import { compressImage, MAX_UPLOAD_BYTES } from '../../utils/imageCompress';
import './scorekeeping.css';

/**
 * P2 to P4: entering a score, what it did, and the account offer.
 *
 * One page in three steps, because it is one moment. Somebody is standing at a
 * machine holding a phone in one hand, having just finished a game. Every tap
 * between them and a recorded score is a tap they might not take.
 *
 * The parts that are load-bearing rather than decorative:
 *
 *   - **The name field is the whole data quality problem.** One player appearing
 *     as two, because the name they typed does not match the name their automatic
 *     scores arrive under, is the defect this system exists to stop. A member's
 *     name is filled in and not editable here. A guest is asked for a Stern
 *     username explicitly, with a quiet way out for the minority who have none.
 *   - **Two guards against the extra zero.** The score echoes back grouped, and
 *     again in words, and the form says where it would land. A wrong number
 *     usually shows up as an implausible position.
 *   - **The confirmation leads with the best true thing.** A beginner who came
 *     last is not told so in the largest type on the page, but is still told,
 *     because hiding it would be patronising.
 */

const compress = (file) => compressImage(file, 'score.jpg');

function SubmitScore() {
  const { machineId } = useParams();
  const navigate = useNavigate();
  // authService.getCurrentUser() parses localStorage and hands back a fresh object
  // every call, so it has a new identity on every render. Read once and hold it:
  // anything that takes it as a dependency otherwise rebuilds on every render, and
  // a fetch keyed on that turns into an unbounded request loop.
  const currentUser = useMemo(() => authService.getCurrentUser(), []);

  const [page, setPage] = useState(null);
  const [step, setStep] = useState('entry');
  const [result, setResult] = useState(null);

  const [score, setScore] = useState('');
  const [guestName, setGuestName] = useState('');
  const [noSternUsername, setNoSternUsername] = useState(false);
  const [nameWarning, setNameWarning] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [originalSize, setOriginalSize] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const cameraRef = useRef(null);
  // Two inputs rather than one. `capture` is what makes a phone open the camera
  // straight away, and it is also what stops the OS offering the photo library,
  // so a single input can have one or the other but not both.
  const libraryRef = useRef(null);

  useEffect(() => {
    getJson(`/machines/${machineId}/page`).then(setPage).catch(() => setPage(null));
  }, [machineId]);

  // The member-name guard runs as soon as the guest stops typing, not at submit,
  // so the wall arrives before the work rather than after it.
  const checkName = useCallback(async (name) => {
    if (!name.trim() || noSternUsername) return setNameWarning(null);
    try {
      const data = await getJson(`/name-check?name=${encodeURIComponent(name)}`);
      setNameWarning(data.belongsToMember ? name.trim() : null);
    } catch {
      setNameWarning(null);
    }
  }, [noSternUsername]);

  const handlePhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setOriginalSize(file.size);
    setCompressing(true);
    try {
      const shrunk = await compress(file);
      if (shrunk.size > MAX_UPLOAD_BYTES) {
        setErrors(['That photo is too large even after shrinking. Try taking it again.']);
        setPhoto(null);
      } else {
        setPhoto(shrunk);
        setPhotoPreview(URL.createObjectURL(shrunk));
        setErrors([]);
      }
    } catch {
      // Compression is an optimisation, not a gate. If the browser cannot do it,
      // send the original and let the server's size limit decide.
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    } finally {
      setCompressing(false);
      // Clear the input so picking the same file again still fires a change.
      event.target.value = '';
    }
  };

  const numericScore = Number(String(score).replace(/[^\d]/g, ''));
  const ready = numericScore > 0 && photo && !compressing && (currentUser || guestName.trim());

  /** Where this score would land, from the board already on screen. */
  const projected = useMemo(() => {
    if (!page || !numericScore) return null;
    const others = page.entries.filter((e) => !(currentUser && e.userId === currentUser.id));
    const position = others.filter((e) => Number(e.score) > numericScore).length + 1;
    return { position, of: Math.max(others.length + 1, position) };
  }, [page, numericScore, currentUser]);

  const submit = async (event) => {
    event.preventDefault();
    const found = [];
    if (!numericScore) found.push('Put in the score from the display.');
    if (!photo) found.push('Every manual score needs a photo of the display.');
    if (!currentUser && !guestName.trim()) found.push('Add the name your score should appear under.');
    if (found.length) return setErrors(found);

    setSubmitting(true);
    setErrors([]);

    const body = new FormData();
    body.append('score', String(numericScore));
    body.append('photo', photo);
    if (!currentUser) body.append('playerName', guestName.trim());

    try {
      const data = await postJson(`/machines/${machineId}/scores`, body);
      const after = await getJson(`/machines/${machineId}/page`).catch(() => page);
      setResult({ submission: data, after, previousBest: page?.myBest || null });
      setStep('done');
    } catch (err) {
      if (err.code === 'NAME_BELONGS_TO_MEMBER') setNameWarning(guestName.trim());
      // The score and the photo are deliberately kept through the failure, so
      // nobody has to type or photograph anything twice.
      setErrors([err.message || 'That did not go through. Check your signal and try again.']);
    } finally {
      setSubmitting(false);
    }
  };

  if (!page) return <div className="sk-page"><div className="sk-shell sk-muted">Loading…</div></div>;

  if (!page.machine.acceptsManualEntry) {
    return (
      <div className="sk-page">
        {!currentUser && <PublicHeader signInRedirect={`/scorekeeping/machines/${machineId}/submit`} />}
        <div className="sk-shell">
          <h1 className="sk-h1">{page.machine.name}</h1>
          <div className="sk-card sk-automatic">
            <h2 className="sk-h2">This machine records your score for you</h2>
            <p className="sk-muted">
              Sign in to Stern Insider Connected at the machine before your last ball drains and your
              score reaches the board on its own.
            </p>
          </div>
          <DSBackLink tone="subtle" size="sm" to={`/scorekeeping/machines/${machineId}`}>
            Back to the machine
          </DSBackLink>
        </div>
      </div>
    );
  }

  if (step === 'done' && result) return renderConfirmation();

  return (
    <div className="sk-page">
      {!currentUser && <PublicHeader signInRedirect={`/scorekeeping/machines/${machineId}/submit`} />}

      <form className="sk-shell" onSubmit={submit}>
        {/* The way back belongs in the page, not in a bar of its own. A signed-in
            member already has the application's header above this, and a second
            full-width bar under it reads as chrome from another application. */}
        <DSBackLink tone="subtle" size="sm" to={`/scorekeeping/machines/${machineId}`}>
          {page.machine.name}
        </DSBackLink>
        <h1 className="sk-h1">Your score</h1>

        {errors.length > 0 && (
          <div className="sk-banner sk-banner--error">
            <strong>{errors.length === 1 ? 'One thing to fix' : `${errors.length} things to fix`}</strong>
            {errors.map((message) => <span key={message}>{message}</span>)}
          </div>
        )}

        {currentUser ? (
          /* The locked name reads as reassurance, not denial. No disabled input:
             a filled card that says what is happening and where to change it. */
          <div className="sk-locked">
            <p className="sk-eyebrow">Submitting as</p>
            <p className="sk-locked__name">@{currentUser.sternUsername || currentUser.stern_username || currentUser.username}</p>
            <p className="sk-muted">
              Your Stern username, so this joins up with your automatic scores.{' '}
              <Link to="/my-membership">Change it in your profile.</Link>
            </p>
          </div>
        ) : (
          <div className="sk-field">
            <label htmlFor="sk-name">
              {noSternUsername ? 'The name to put on the board' : 'Your Stern username'}
            </label>
            <input
              id="sk-name"
              className={`sk-input ${nameWarning ? 'has-error' : ''}`}
              value={guestName}
              onChange={(e) => { setGuestName(e.target.value); setNameWarning(null); }}
              onBlur={(e) => checkName(e.target.value)}
              autoComplete="off"
              placeholder={noSternUsername ? 'Paula' : 'PinballPaula'}
            />

            {/* What the wireframe leads with, and the reason the field exists.
                Somebody typing their real name here is the single mistake that
                splits one player into two, so the field says what a Stern
                username is rather than assuming they know. */}
            {!noSternUsername && (
              <p className="sk-muted sk-field__note">
                The name that shows on a Stern machine when you sign in. Not your real name, not
                your initials. Getting this right is what keeps your scores together.
              </p>
            )}

            {nameWarning ? (
              <div className="sk-namewarn">
                <p>That name is already a member&apos;s.</p>
                <div className="sk-namewarn__actions">
                  <Link
                    className="sk-btn sk-btn--small"
                    to={`/login?redirect=/scorekeeping/machines/${machineId}/submit`}
                  >
                    Sign in as {nameWarning}
                  </Link>
                  <button
                    type="button"
                    className="sk-link"
                    onClick={() => { setGuestName(''); setNameWarning(null); }}
                  >
                    Use a different name
                  </button>
                </div>
                {numericScore > 0 && <p className="sk-muted">Kept. You won&apos;t have to type it again.</p>}
              </div>
            ) : (
              <button
                type="button"
                className="sk-link sk-link--quiet"
                onClick={() => { setNoSternUsername(!noSternUsername); setNameWarning(null); }}
              >
                {noSternUsername ? 'I do have one after all' : "I don't have a Stern username"}
              </button>
            )}

            {noSternUsername && (
              <p className="sk-muted sk-field__note">
                Fine for today. Without a Stern username the club can&apos;t join this up with scores
                the machines report automatically.
              </p>
            )}

            {/* Leads with what it costs rather than with the ask. Somebody
                standing at a machine reads "sign in" as a wall between them and
                a score they have already earned, and the wireframe answers that
                before it invites them. */}
            <div className="sk-signin-note">
              <span className="sk-signin-note__free">Free</span>
              <span>
                Already a member?{' '}
                <Link to={`/login?redirect=/scorekeeping/machines/${machineId}/submit`}>
                  Sign in
                </Link>
                {' '}and your name fills itself in.
              </span>
            </div>
          </div>
        )}

        <div className="sk-field">
          <label htmlFor="sk-score">Score</label>
          <input
            id="sk-score"
            className="sk-input sk-input--score"
            inputMode="numeric"
            pattern="[0-9,]*"
            value={numericScore ? formatScore(numericScore) : score}
            onChange={(e) => setScore(e.target.value)}
            placeholder="0"
          />
          {/* First guard against the extra zero. */}
          {inWords(numericScore) ? (
            <p className="sk-muted sk-field__note">
              Reads as {inWords(numericScore)}. Check the zeros.
            </p>
          ) : (
            <p className="sk-muted sk-field__note">Number pad only. Digits group as you type.</p>
          )}
        </div>

        <div className="sk-field">
          <label htmlFor="sk-photo">Photo of the display</label>
          {/* The native control is kept for the label, the keyboard and assistive
              technology, and hidden, because "Choose File / No file chosen" is
              the wrong instruction for somebody who is about to use a camera. */}
          <input
            id="sk-photo"
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sk-file"
            onChange={handlePhoto}
          />
          <input
            ref={libraryRef}
            type="file"
            accept="image/*"
            className="sk-file"
            tabIndex={-1}
            aria-hidden="true"
            onChange={handlePhoto}
          />

          {!photo && !compressing && (
            <>
              <button type="button" className="sk-photobtn" onClick={() => cameraRef.current?.click()}>
                Take a photo
              </button>
              {/* Quieter, because photographing the display now is the flow this
                  page is for. But somebody who already took the shot, or whose
                  camera would not open, should not be stuck. */}
              <button
                type="button"
                className="sk-link sk-link--quiet"
                onClick={() => libraryRef.current?.click()}
              >
                Choose an existing photo
              </button>
            </>
          )}

          {compressing && (
            <div className="sk-photostate">
              <p className="sk-photostate__title">Shrinking the photo</p>
              <p className="sk-muted">
                {originalSize ? `${(originalSize / 1048576).toFixed(1)} MB down to about 400 KB. ` : ''}
                Works on weak signal.
              </p>
            </div>
          )}

          {photo && !compressing && (
            <div className="sk-photostate sk-photostate--ready">
              {photoPreview && <img src={photoPreview} alt="The score you photographed" />}
              <div>
                <p className="sk-photostate__title">Photo ready</p>
                <p className="sk-muted">{Math.round(photo.size / 1024)} KB after compression</p>
                <div className="sk-photostate__actions">
                  <button type="button" className="sk-link" onClick={() => cameraRef.current?.click()}>
                    Retake
                  </button>
                  <button type="button" className="sk-link" onClick={() => libraryRef.current?.click()}>
                    Choose another
                  </button>
                </div>
              </div>
            </div>
          )}

          {!photo && !compressing && (
            <p className="sk-muted sk-field__note">
              Required. The score on the screen is what makes it count.
            </p>
          )}
        </div>

        {/* Second guard: a wrong number usually lands in an implausible position. */}
        {projected && photo && (
          <div className="sk-projection">
            This would put you <strong>{ordinal(projected.position)} of {projected.of}</strong>
          </div>
        )}

        <button type="submit" className="sk-cta" disabled={!ready || submitting}>
          {submitting ? 'Submitting…' : 'Submit score'}
        </button>
        {submitting && (
          <p className="sk-cta__note">Don&apos;t leave the page. This takes a moment on club wifi.</p>
        )}
        {!ready && !submitting && (
          <p className="sk-cta__note">
            {nameWarning ? 'Sort the name out and this unlocks.' : 'Add a score and a photo to submit.'}
          </p>
        )}
      </form>
    </div>
  );

  /**
   * P3. What the score did.
   *
   * The headline is the best true thing about it. A position when the position is
   * good, an improvement when there is a previous best to improve on, and plain
   * arrival when there is neither.
   */
  function renderConfirmation() {
    const { submission, after, previousBest } = result;
    const mine = after.entries.find((e) =>
      (currentUser && e.userId === currentUser.id) ||
      (!currentUser && e.playerName.toLowerCase() === submission.playerName.toLowerCase())
    );
    const position = mine?.rank;
    const improved = previousBest && submission.score > previousBest.score
      ? Math.round(((submission.score - previousBest.score) / previousBest.score) * 100)
      : null;

    const headline = improved !== null
      ? 'Your best yet'
      : (position && position <= 3 ? `${ordinal(position)} on ${after.machine.name}` : 'On the board');

    const index = after.entries.findIndex((e) => e === mine);
    const neighbours = index >= 0
      ? after.entries.slice(Math.max(0, index - 1), index + 3)
      : after.entries.slice(0, 3);

    return (
      <div className="sk-page">
        {!currentUser && <PublicHeader signInRedirect="/scorekeeping/leaderboard" />}
        <div className="sk-shell">
          <div className="sk-confirm">
            <span className="sk-confirm__tick">✓</span>
            <h1 className="sk-h1">{headline}</h1>
            {improved !== null ? (
              <p className="sk-confirm__sub">
                Up {improved}% on your old best. {formatScore(submission.score)} on {after.machine.name}.
                Your best was {formatScore(previousBest.score)}.
              </p>
            ) : (
              <p className="sk-confirm__sub">
                {formatScore(submission.score)} for {submission.playerName}.
              </p>
            )}
          </div>

          <div className="sk-stats">
            {position && (
              <div className="sk-stat">
                <span className="sk-stat__label">On this machine</span>
                <span className="sk-stat__value">{ordinal(position)}</span>
              </div>
            )}
            {mine?.points > 0 && (
              <div className="sk-stat">
                <span className="sk-stat__label">Points</span>
                <span className="sk-stat__value">{mine.points}</span>
              </div>
            )}
            {!submission.countsTowardCompetition && (
              <div className="sk-stat">
                <span className="sk-stat__label">This machine</span>
                <span className="sk-stat__value sk-stat__value--muted">Not counting</span>
              </div>
            )}
          </div>

          {/* The club competes on improving your own number, so a target you can
              actually reach leads over a gap to somebody else. */}
          {position === after.entries.length && after.entries.length > 1 && (
            <div className="sk-card sk-aim">
              <p className="sk-eyebrow">Something to aim at</p>
              <p className="sk-aim__target">
                Get to {formatScore(Math.round(submission.score * 1.33))} and you&apos;ve improved by a third.
              </p>
              <p className="sk-muted">
                Where you are: {ordinal(position)} of {after.entries.length}
              </p>
            </div>
          )}

          <section className="sk-card">
            <table className="sk-board">
              <tbody>
                {neighbours.map((entry) => (
                  <tr key={`${entry.rank}-${entry.playerName}`} className={entry === mine ? 'is-me' : ''}>
                    <td>#{entry.rank}</td>
                    <td className="sk-board__player">
                      {entry.playerName}{entry === mine ? ' (you)' : ''}
                    </td>
                    <td className="sk-num">{formatScore(entry.score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {!currentUser && (
            <section className="sk-card sk-offer">
              <p className="sk-offer__title">Keep this, and everything after it</p>
              <p className="sk-muted">
                Next time you play {after.machine.name}, we&apos;ll tell you whether you beat today.
                Your name, an email address and a password.
              </p>
              <Link
                className="sk-cta sk-cta--inline"
                to={`/scorekeeping/signup?name=${encodeURIComponent(submission.playerName)}&machine=${machineId}`}
              >
                Create an account
              </Link>
              <p className="sk-muted sk-offer__note">Your score is already saved either way.</p>
            </section>
          )}

          {/* One way on from here. Somebody who has just posted a score wants to
              see where it landed, and putting up a second score means walking to
              another machine, which is not a decision this page has to offer.
              A guest gets it as the quieter option, because the account offer
              above it is the thing worth their next tap. */}
          <div className="sk-actions">
            <Link
              className={currentUser ? 'sk-cta' : 'sk-btn'}
              to={`/scorekeeping/machines/${machineId}`}
            >
              See the full board
            </Link>
            {currentUser && (
              <button
                type="button"
                className="sk-link sk-link--danger"
                onClick={async () => {
                  await del(`/scores/${submission.id}/mine`).catch(() => {});
                  navigate(`/scorekeeping/machines/${machineId}`);
                }}
              >
                Wrong number? Remove this score
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default SubmitScore;
